/**
 * Click-to-WhatsApp source-code mint client.
 *
 * On page load (at browser idle, never on the critical path) we send the
 * captured attribution context — query params, campaign tk, GA client id —
 * to the source-resolver API, which stores it and returns a short code.
 * The code is cached and later embedded invisibly in the WhatsApp pre-fill
 * (see ./token.ts, ./waLink.ts).
 *
 * Fail-open by design: any error, timeout, or the feature flag being off
 * simply means links are built without a code, exactly as they are today.
 */

import { captureAndStoreQueryParamsBase64 } from "@/lib/queryParams";
import { handleCampaignID } from "@/lib/utils";

const ENABLED = true;
const MINT_URL: string =
  "https://9fcpu69d57.execute-api.ap-south-1.amazonaws.com/prod/generate-code";

const CODE_KEY = "ctw_code";
const FINGERPRINT_KEY = "ctw_fp";
const MINTED_AT_KEY = "ctw_minted_at";
const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;
const MINT_TIMEOUT_MS = 4000; // runs at idle; bounds the promise, not the UI

const CODE_EVENT = "ctw:code";

const ATTRIBUTION_PARAMS = [
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
];

let mintInFlight: Promise<string | null> | null = null;

export function isCtwEnabled(): boolean {
  return ENABLED && MINT_URL !== "" && typeof window !== "undefined";
}

/** Synchronous cache read — safe to call from render paths. */
export function getCachedCtwCode(): string | null {
  if (!isCtwEnabled()) return null;
  try {
    const code = localStorage.getItem(CODE_KEY);
    const mintedAt = Number(localStorage.getItem(MINTED_AT_KEY));
    if (!code || !mintedAt) return null;
    if (Date.now() - mintedAt > FOURTEEN_DAYS_MS) return null;
    return /^[0-9a-f]{12}$/i.test(code) ? code : null;
  } catch {
    return null;
  }
}

/**
 * Ensures a code exists for the current attribution context, minting one if
 * needed. Re-mints when the visitor arrives with different campaign params;
 * reuses the cached code inside the 14-day window otherwise (mirroring the
 * freshness semantics of handleCampaignID's tk handling).
 */
export function ensureCtwCode(): Promise<string | null> {
  if (!isCtwEnabled()) return Promise.resolve(null);
  if (mintInFlight) return mintInFlight;

  const params = currentAttributionParams();
  const fingerprint = fingerprintOf(params);
  const cached = getCachedCtwCode();

  if (cached) {
    let cachedFingerprint: string | null = null;
    try {
      cachedFingerprint = localStorage.getItem(FINGERPRINT_KEY);
    } catch {
      /* unreadable storage — fall through to re-mint */
    }
    const noNewAttribution = Object.keys(params).length === 0;
    if (noNewAttribution || cachedFingerprint === fingerprint) {
      return Promise.resolve(cached);
    }
  }

  mintInFlight = mint(fingerprint).finally(() => {
    mintInFlight = null;
  });
  return mintInFlight;
}

async function mint(fingerprint: string): Promise<string | null> {
  try {
    const payload: Record<string, string> = {};

    const blob = captureAndStoreQueryParamsBase64();
    if (blob) payload.blob = blob;

    const tk = handleCampaignID();
    if (tk) payload.tk = tk;

    const gaClientId = readGaClientId();
    if (gaClientId) payload.ga_client_id = gaClientId;

    payload.landing_url = window.location.href.slice(0, 2048);
    if (document.referrer) payload.referrer = document.referrer.slice(0, 2048);

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), MINT_TIMEOUT_MS);
    const response = await fetch(MINT_URL, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
      signal: controller.signal,
      keepalive: true,
    });
    clearTimeout(timer);

    if (!response.ok) return null;
    const data = await response.json();
    const code = typeof data?.code === "string" ? data.code : null;
    if (!code || !/^[0-9a-f]{12}$/i.test(code)) return null;

    try {
      localStorage.setItem(CODE_KEY, code);
      localStorage.setItem(FINGERPRINT_KEY, fingerprint);
      localStorage.setItem(MINTED_AT_KEY, Date.now().toString());
    } catch {
      /* storage unavailable — the code still works for this pageview */
    }

    window.dispatchEvent(new CustomEvent(CODE_EVENT));
    return code;
  } catch {
    return null;
  }
}

/** Subscribe to "a new code became available"; returns an unsubscribe fn. */
export function onCtwCode(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(CODE_EVENT, listener);
  return () => window.removeEventListener(CODE_EVENT, listener);
}

function currentAttributionParams(): Record<string, string> {
  const result: Record<string, string> = {};
  const search = new URLSearchParams(window.location.search);
  for (const key of ATTRIBUTION_PARAMS) {
    const value = search.get(key);
    if (value) result[key] = value;
  }
  return result;
}

function fingerprintOf(params: Record<string, string>): string {
  const joined = ATTRIBUTION_PARAMS.map((k) => params[k] ?? "").join("|");
  let hash = 5381;
  for (let i = 0; i < joined.length; i++) {
    hash = ((hash << 5) + hash + joined.charCodeAt(i)) >>> 0;
  }
  return hash.toString(16);
}

export function readGaClientId(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
  if (!match) return null;
  // _ga looks like GA1.1.1234567890.1699999999 — client id is the last two segments
  const parts = decodeURIComponent(match[1]).split(".");
  if (parts.length < 4) return null;
  return parts.slice(-2).join(".");
}
